/**
 * AdminModerationPage - Manage preset approvals and marketplace moderation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronLeft,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Search,
  Filter,
  Eye,
  Music,
  Sliders,
  Users,
  Download,
  Package,
  AlertTriangle,
  Sparkles,
  BarChart3,
  RefreshCw,
  Trash2,
  Square,
  CheckSquare,
  ListChecks,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin emails - authorized users for admin panel
const ADMIN_EMAILS = [
  'turnersouthsyndicate@gmail.com',
  // Add more admin emails as needed
];

// Stats Card
const StatCard = ({ title, value, icon: Icon, color = 'violet' }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-sm text-zinc-400">{title}</div>
      </div>
    </div>
  </div>
);

// Preset Row for Admin
const AdminPresetRow = ({ preset, onApprove, onReject, onToggleFeatured, onView, isSelected, onToggleSelect }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const handleApprove = async () => {
    setProcessing(true);
    try {
      await onApprove(preset.id);
      toast.success(`Approved: ${preset.name}`);
    } catch (error) {
      toast.error('Failed to approve preset');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async () => {
    if (rejectReason.length < 10) {
      toast.error('Please provide a rejection reason (min 10 characters)');
      return;
    }
    setProcessing(true);
    try {
      await onReject(preset.id, rejectReason);
      toast.success(`Rejected: ${preset.name}`);
      setShowRejectForm(false);
    } catch (error) {
      toast.error('Failed to reject preset');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleToggleFeatured = async () => {
    setProcessing(true);
    try {
      await onToggleFeatured(preset.id);
      toast.success(`${preset.is_featured ? 'Unfeatured' : 'Featured'}: ${preset.name}`);
    } catch (error) {
      toast.error('Failed to update featured status');
    } finally {
      setProcessing(false);
    }
  };
  
  const getStatusBadge = () => {
    switch (preset.status) {
      case 'approved':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-zinc-900/50 border rounded-xl p-4 transition-colors ${
        isSelected ? 'border-violet-500 bg-violet-500/10' : 'border-zinc-800'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Selection checkbox for pending presets */}
        {preset.status === 'pending' && (
          <button
            onClick={() => onToggleSelect(preset.id)}
            className="mt-2 flex-shrink-0 text-zinc-400 hover:text-white transition-colors"
            data-testid={`select-${preset.id}`}
          >
            {isSelected ? (
              <CheckSquare className="w-5 h-5 text-violet-400" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
        )}
        
        {/* Icon */}
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          preset.preset_type === 'master' ? 'bg-amber-500/20' : 'bg-cyan-500/20'
        }`}>
          {preset.preset_type === 'master' ? (
            <Sliders className="w-6 h-6 text-amber-400" />
          ) : (
            <Music className="w-6 h-6 text-cyan-400" />
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">{preset.name}</h3>
            {getStatusBadge()}
            {preset.is_featured && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-violet-500/20 text-violet-400 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Featured
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 line-clamp-1 mb-1">
            {preset.description || 'No description'}
          </p>
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span>By: {preset.author_name}</span>
            <span>•</span>
            <span>{new Date(preset.created_at).toLocaleDateString()}</span>
            {preset.download_count > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Download className="w-3 h-3" />
                  {preset.download_count}
                </span>
              </>
            )}
          </div>
          
          {/* Tags */}
          {preset.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {preset.tags.slice(0, 5).map((tag, i) => (
                <span key={i} className="px-2 py-0.5 text-xs bg-zinc-800 rounded-full text-zinc-400">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Price */}
        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
          (preset.price || 0) === 0
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-violet-500/20 text-violet-400'
        }`}>
          {(preset.price || 0) === 0 ? 'FREE' : `$${(preset.price || 0).toFixed(2)}`}
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-2">
          {preset.status === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid={`approve-${preset.id}`}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRejectForm(!showRejectForm)}
                disabled={processing}
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                data-testid={`reject-btn-${preset.id}`}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </>
          )}
          
          {preset.status === 'approved' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleToggleFeatured}
              disabled={processing}
              className={preset.is_featured 
                ? 'border-violet-500 text-violet-400' 
                : 'border-zinc-700 text-zinc-400'}
              data-testid={`feature-${preset.id}`}
            >
              <Star className={`w-4 h-4 mr-1 ${preset.is_featured ? 'fill-violet-400' : ''}`} />
              {preset.is_featured ? 'Unfeature' : 'Feature'}
            </Button>
          )}
          
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onView(preset.id)}
            className="text-zinc-400"
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
        </div>
      </div>
      
      {/* Reject Form */}
      <AnimatePresence>
        {showRejectForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-zinc-800"
          >
            <label className="text-sm text-zinc-400 mb-2 block">Rejection Reason</label>
            <div className="flex gap-2">
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this preset is being rejected (min 10 chars)"
                className="flex-1 bg-zinc-800/50 border-zinc-700"
                data-testid={`reject-reason-${preset.id}`}
              />
              <Button
                onClick={handleReject}
                disabled={processing || rejectReason.length < 10}
                className="bg-red-600 hover:bg-red-500"
                data-testid={`confirm-reject-${preset.id}`}
              >
                Confirm
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason('');
                }}
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function AdminModerationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [presets, setPresets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  
  // Check admin access based on email whitelist
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());
  
  // Get pending presets for bulk selection
  const pendingPresets = presets.filter(p => p.status === 'pending');
  const allPendingSelected = pendingPresets.length > 0 && pendingPresets.every(p => selectedIds.has(p.id));
  const somePendingSelected = pendingPresets.some(p => selectedIds.has(p.id));
  
  // Toggle single selection
  const toggleSelect = (presetId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(presetId)) {
        newSet.delete(presetId);
      } else {
        newSet.add(presetId);
      }
      return newSet;
    });
  };
  
  // Toggle select all pending
  const toggleSelectAll = () => {
    if (allPendingSelected) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // Select all pending
      setSelectedIds(new Set(pendingPresets.map(p => p.id)));
    }
  };
  
  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) {
      toast.error('No presets selected');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/marketplace/admin/presets/bulk-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset_ids: Array.from(selectedIds),
          admin_id: user.id,
          admin_email: user.email
        })
      });
      
      if (!response.ok) {
        throw new Error('Bulk approve failed');
      }
      
      const data = await response.json();
      toast.success(`Approved ${data.approved_count} presets`);
      
      // Clear selection and refresh
      setSelectedIds(new Set());
      fetchPresets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to bulk approve presets');
    } finally {
      setBulkProcessing(false);
    }
  };
  
  // Bulk reject
  const handleBulkReject = async () => {
    if (selectedIds.size === 0) {
      toast.error('No presets selected');
      return;
    }
    
    if (bulkRejectReason.length < 10) {
      toast.error('Please provide a rejection reason (min 10 characters)');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/marketplace/admin/presets/bulk-reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset_ids: Array.from(selectedIds),
          admin_id: user.id,
          admin_email: user.email,
          reason: bulkRejectReason
        })
      });
      
      if (!response.ok) {
        throw new Error('Bulk reject failed');
      }
      
      const data = await response.json();
      toast.success(`Rejected ${data.rejected_count} presets`);
      
      // Clear selection and close modal
      setSelectedIds(new Set());
      setShowBulkRejectModal(false);
      setBulkRejectReason('');
      fetchPresets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to bulk reject presets');
    } finally {
      setBulkProcessing(false);
    }
  };
  
  // Fetch admin stats
  const fetchStats = useCallback(async () => {
    if (!user || !isAdmin) return;
    
    try {
      const response = await fetch(`${API_URL}/api/marketplace/admin/stats?admin_id=${user.id}&admin_email=${encodeURIComponent(user.email)}`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [user, isAdmin]);
  
  // Fetch presets
  const fetchPresets = useCallback(async () => {
    if (!user || !isAdmin) return;
    
    setLoading(true);
    try {
      const emailParam = `&admin_email=${encodeURIComponent(user.email)}`;
      const url = statusFilter === 'pending'
        ? `${API_URL}/api/marketplace/admin/pending?admin_id=${user.id}${emailParam}&page=${page}&limit=20`
        : `${API_URL}/api/marketplace/admin/all-presets?admin_id=${user.id}${emailParam}&status=${statusFilter !== 'all' ? statusFilter : ''}&page=${page}&limit=20`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, statusFilter, page]);
  
  // Initial load
  useEffect(() => {
    fetchStats();
    fetchPresets();
  }, [fetchStats, fetchPresets]);
  
  // Handle approve
  const handleApprove = async (presetId) => {
    const response = await fetch(
      `${API_URL}/api/marketplace/admin/presets/${presetId}/approve?admin_id=${user.id}&admin_email=${encodeURIComponent(user.email)}`,
      { method: 'POST' }
    );
    
    if (!response.ok) {
      throw new Error('Approve failed');
    }
    
    // Refresh data
    fetchPresets();
    fetchStats();
  };
  
  // Handle reject
  const handleReject = async (presetId, reason) => {
    const response = await fetch(
      `${API_URL}/api/marketplace/admin/presets/${presetId}/reject?admin_id=${user.id}&admin_email=${encodeURIComponent(user.email)}&reason=${encodeURIComponent(reason)}`,
      { method: 'POST' }
    );
    
    if (!response.ok) {
      throw new Error('Reject failed');
    }
    
    // Refresh data
    fetchPresets();
    fetchStats();
  };
  
  // Handle toggle featured
  const handleToggleFeatured = async (presetId) => {
    const response = await fetch(
      `${API_URL}/api/marketplace/admin/presets/${presetId}/feature?admin_id=${user.id}&admin_email=${encodeURIComponent(user.email)}`,
      { method: 'POST' }
    );
    
    if (!response.ok) {
      throw new Error('Feature toggle failed');
    }
    
    // Refresh data
    fetchPresets();
    fetchStats();
  };
  
  // Handle view
  const handleView = (presetId) => {
    window.open(`/marketplace/${presetId}`, '_blank');
  };
  
  // Filter presets by search
  const filteredPresets = presets.filter(preset => 
    !searchQuery || 
    preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    preset.author_name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Access denied - not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Please log in to access admin panel</p>
          <Button onClick={() => navigate('/')} className="bg-violet-600 hover:bg-violet-500">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }
  
  // Access denied - not an admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
          <p className="text-zinc-400 mb-4">You don't have permission to access the admin panel.</p>
          <p className="text-sm text-zinc-500 mb-6">Logged in as: {user.email}</p>
          <Button onClick={() => navigate('/marketplace')} className="bg-violet-600 hover:bg-violet-500">
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900/80 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/marketplace')}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-400" />
                  Admin Moderation
                </h1>
                <p className="text-sm text-zinc-400">Review and manage marketplace presets</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { fetchStats(); fetchPresets(); }}
              className="text-zinc-400 hover:text-white"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
            <StatCard title="Total" value={stats.total_presets} icon={Package} color="violet" />
            <StatCard title="Pending" value={stats.pending} icon={Clock} color="amber" />
            <StatCard title="Approved" value={stats.approved} icon={CheckCircle} color="emerald" />
            <StatCard title="Rejected" value={stats.rejected} icon={XCircle} color="red" />
            <StatCard title="Featured" value={stats.featured} icon={Star} color="violet" />
            <StatCard title="Downloads" value={stats.total_downloads} icon={Download} color="cyan" />
            <StatCard title="Purchases" value={stats.total_purchases} icon={BarChart3} color="emerald" />
            <StatCard title="Creators" value={stats.unique_creators} icon={Users} color="blue" />
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 pb-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or author..."
              className="pl-10 bg-zinc-900/50 border-zinc-800"
              data-testid="admin-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); setSelectedIds(new Set()); }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-violet-500"
            data-testid="admin-status-filter"
          >
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All Presets</option>
          </select>
        </div>
        
        {/* Bulk Action Bar - Only show when viewing pending presets */}
        {statusFilter === 'pending' && pendingPresets.length > 0 && (
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="select-all-btn"
                >
                  {allPendingSelected ? (
                    <CheckSquare className="w-5 h-5 text-violet-400" />
                  ) : somePendingSelected ? (
                    <ListChecks className="w-5 h-5 text-violet-400" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                  <span className="text-sm">
                    {allPendingSelected ? 'Deselect All' : 'Select All'}
                  </span>
                </button>
                {selectedIds.size > 0 && (
                  <span className="text-sm text-violet-400 font-medium">
                    {selectedIds.size} selected
                  </span>
                )}
              </div>
              
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleBulkApprove}
                    disabled={bulkProcessing}
                    className="bg-emerald-600 hover:bg-emerald-500"
                    data-testid="bulk-approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Selected ({selectedIds.size})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulkRejectModal(true)}
                    disabled={bulkProcessing}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    data-testid="bulk-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Selected ({selectedIds.size})
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Bulk Reject Modal */}
        <AnimatePresence>
          {showBulkRejectModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
              onClick={() => setShowBulkRejectModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-md w-full"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-white mb-2">Bulk Reject Presets</h3>
                <p className="text-sm text-zinc-400 mb-4">
                  You are about to reject {selectedIds.size} presets. Please provide a reason.
                </p>
                <Input
                  value={bulkRejectReason}
                  onChange={(e) => setBulkRejectReason(e.target.value)}
                  placeholder="Rejection reason (min 10 characters)"
                  className="bg-zinc-800/50 border-zinc-700 mb-4"
                  data-testid="bulk-reject-reason"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowBulkRejectModal(false);
                      setBulkRejectReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleBulkReject}
                    disabled={bulkProcessing || bulkRejectReason.length < 10}
                    className="bg-red-600 hover:bg-red-500"
                    data-testid="confirm-bulk-reject"
                  >
                    {bulkProcessing ? 'Processing...' : `Reject ${selectedIds.size} Presets`}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Preset List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl h-32 animate-pulse" />
            ))}
          </div>
        ) : filteredPresets.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-400 mb-2">
              {statusFilter === 'pending' ? 'No pending presets' : 'No presets found'}
            </h3>
            <p className="text-zinc-500">
              {statusFilter === 'pending' 
                ? 'All presets have been reviewed!'
                : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {filteredPresets.map(preset => (
                <AdminPresetRow
                  key={preset.id}
                  preset={preset}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onToggleFeatured={handleToggleFeatured}
                  onView={handleView}
                  isSelected={selectedIds.has(preset.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-zinc-700"
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-zinc-400">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-zinc-700"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
